// Advergaming authoring (7.6F, ADR-0022) as typed CreativeOperation[] — the
// same operation language as every Studio edit and as AI authoring. The game
// is the document's declared `game` module (data, never code): starters add a
// complete editable game; the inspector gives layers a role (target, card,
// draggable piece, drop zone); the setup dialog edits goal, time, lives,
// scoring and results. Nothing here validates or repairs: the Creative Engine
// accepts or rejects every batch, and deletions resolve their references
// explicitly (announced by the caller).

import {
  gameStarter,
  type AssetRef,
  type BehaviorUse,
  type CreativeDocument,
  type CreativeOperation,
  type Element,
  type GameDecl,
  type GameStarterKind,
  type GameTarget,
  type StatePatch,
} from "@interactads/creative-model";
import { COMPONENT_ACCENT } from "./components";
import { withoutRole } from "./game-prune";
import { omit } from "./util";
import { editingScene, findElement, nextId, sceneOfElement } from "./model";

export { pruneGame, type GameRemoved } from "./game-prune";

type Ops = CreativeOperation[];
const clone = <T>(v: T): T => JSON.parse(JSON.stringify(v)) as T;

export const GAME_STARTERS: ReadonlyArray<{
  readonly kind: GameStarterKind;
  readonly label: string;
  readonly description: string;
}> = [
  { kind: "find", label: "Encuentra las diferencias", description: "Toca las diferencias entre dos imágenes." },
  { kind: "pairs", label: "Memoria (parejas)", description: "Voltea cartas y encuentra las parejas." },
  { kind: "tap", label: "Atrápalo", description: "Toca el objetivo antes de que salte." },
  { kind: "quiz", label: "Quiz", description: "Preguntas con respuesta correcta y puntaje." },
  { kind: "drag", label: "Arrastra al lugar", description: "Lleva cada pieza a su caja." },
  {
    kind: "runner",
    label: "Carrera (plataformas)",
    description: "El personaje corre: salta, se agacha, esquiva peligros y recoge energía.",
  },
];

/**
 * Adds a complete, editable game (intro → game → win / lose). The intro becomes the start
 * of the creative only when the current start scene is still empty (said by the caller).
 */
export function insertGame(doc: CreativeDocument, kind: GameStarterKind) {
  const start = doc.scenes.find((s) => s.id === doc.initialSceneId);
  const makeInitial = start !== undefined && start.children.length === 0;
  const starter = gameStarter(doc, kind, { accent: COMPONENT_ACCENT, makeInitial });
  return { ...starter, makeInitial };
}

// ─── Roles ──────────────────────────────────────────────────────────────────

export type GameRole =
  | { readonly kind: "target"; readonly target: GameTarget }
  | { readonly kind: "card"; readonly pair: number; readonly partner: string }
  | { readonly kind: "item"; readonly accepts: readonly string[] }
  | { readonly kind: "zone" }
  | { readonly kind: "area"; readonly of: readonly string[] };

export function gameRoleOf(doc: CreativeDocument, id: string): GameRole | null {
  const g = doc.game;
  if (!g) return null;
  const target = g.targets?.find((t) => t.element === id);
  if (target) return { kind: "target", target };
  const pair = g.pairs?.cards.findIndex((p) => p.includes(id)) ?? -1;
  if (pair >= 0) {
    const cards = g.pairs?.cards[pair] ?? [];
    return { kind: "card", pair, partner: cards.find((c) => c !== id) ?? "" };
  }
  if (g.drops?.items.includes(id)) {
    const drag = findElement(doc, id)?.behaviors?.find((b) => b.type === "draggable");
    return { kind: "item", accepts: drag?.type === "draggable" ? (drag.dropTargets ?? []) : [] };
  }
  if (g.drops?.zones.includes(id)) return { kind: "zone" };
  const of = (g.targets ?? []).filter((t) => t.relocate === id).map((t) => t.element);
  if (of.length > 0) return { kind: "area", of };
  return null;
}

/** Writes the module with `patch` applied; `undefined` values remove optional keys. */
export function setGame(doc: CreativeDocument, patch: Partial<Record<keyof GameDecl, unknown>>): Ops {
  const g = doc.game;
  if (!g) return [];
  const next = Object.fromEntries([
    ...Object.entries(clone(g)).filter(([k]) => !Object.hasOwn(patch, k)),
    ...Object.entries(patch).filter(([, v]) => v !== undefined),
  ]);
  return [{ op: "setGame", game: next as GameDecl }];
}

/** Removes the declared game; the layers, scenes and rules stay (ordinary content). */
export const removeGame = (): Ops => [{ op: "setGame" }];

/** A game for a scene, built from scratch (no starter): manual goal, a score variable. */
export function createGame(doc: CreativeDocument, sceneId: string): Ops {
  if (doc.game) return [];
  const taken = [...(doc.variables ?? []).map((v) => v.id), ...doc.elements.map((e) => e.id)];
  const score = taken.includes("score") ? nextId(taken, "score") : "score";
  return [
    {
      op: "setVariable",
      variable: { id: score, type: "number", initial: 0, min: 0, max: 9_999, integer: true, report: "onComplete" },
    },
    {
      op: "setGame",
      game: {
        id: "game",
        scenes: [sceneId],
        start: "onEnter",
        score,
        win: { when: "manual" },
        scoring: { hit: 1, miss: 0, missCostsLife: false },
        results: { delayMs: 600 },
      },
    },
  ];
}

const FOUND_STATE = "found";
const foundPatch = (): StatePatch => ({
  border: { width: 3, color: COMPONENT_ACCENT, style: "solid" },
  fill: { kind: "solid", color: `${COMPONENT_ACCENT}33` },
});

/** The layer stops playing a role (pairs lose the whole pair); nothing else changes. */
export function removeRole(doc: CreativeDocument, id: string): Ops {
  const g = doc.game;
  if (!g || !gameRoleOf(doc, id)) return [];
  const ops: Ops = [{ op: "setGame", game: withoutRole(g, id) as GameDecl }];
  // A piece that stops being a drop item keeps being draggable only if the person wants it: remove the drag.
  if (g.drops?.items.includes(id)) ops.push(...setDraggable(doc, id, null));
  // Zones removed from the game are dropped from every piece that accepted them.
  if (g.drops?.zones.includes(id))
    for (const item of g.drops.items) {
      const accepts = acceptsOf(doc, item).filter((z) => z !== id);
      if (accepts.length !== acceptsOf(doc, item).length) ops.push(...setDraggable(doc, item, accepts));
    }
  return ops;
}

/** Target role (correct / wrong), with a "found" state written explicitly for one-time correct targets. */
export type TargetPatch = { readonly [K in keyof GameTarget]?: GameTarget[K] | undefined };
export function setTarget(doc: CreativeDocument, id: string, patch: TargetPatch): Ops {
  const g = doc.game;
  const el = findElement(doc, id);
  if (!g || !el) return [];
  const base: Record<string, unknown> = { ...clone(withoutRole(g, id)) };
  const current = g.targets?.find((t) => t.element === id) ?? { element: id, role: "correct" as const };
  const merged = Object.fromEntries(
    Object.entries({ ...clone(current), ...patch, element: id }).filter(([, v]) => v !== undefined)
  );
  const target: Record<string, unknown> =
    merged.role === "wrong"
      ? omit(merged, "repeat", "foundState")
      : merged.repeat === true
        ? omit(merged, "foundState")
        : merged;
  const ops: Ops = [];
  const wantsFound = target.role === "correct" && target.repeat !== true;
  if (wantsFound && target.foundState === undefined && !("foundState" in patch)) {
    target.foundState = FOUND_STATE;
    if (!Object.hasOwn(el.states ?? {}, FOUND_STATE))
      ops.push({ op: "updateElement", id, set: { states: { ...(el.states ?? {}), [FOUND_STATE]: foundPatch() } } });
  }
  const targets = [...((base.targets as GameTarget[] | undefined) ?? [])];
  const at = (g.targets ?? []).findIndex((t) => t.element === id);
  targets.splice(at < 0 ? targets.length : at, 0, target as unknown as GameTarget);
  base.targets = targets;
  return [...ops, { op: "setGame", game: base as GameDecl }];
}

/** Makes two layers a pair (each leaves any other role). Creates the pairs block with Studio defaults. */
export function pairCards(doc: CreativeDocument, a: string, b: string): Ops {
  const g = doc.game;
  if (!g || a === b || !findElement(doc, a) || !findElement(doc, b)) return [];
  const base = withoutRole(withoutRole(g, a) as GameDecl, b);
  const existing = g.pairs;
  const cards = [...(existing?.cards ?? []).filter((p) => !p.includes(a) && !p.includes(b)), [a, b]];
  base.pairs = {
    cover: existing?.cover ?? { color: "#1F2937", accent: COMPONENT_ACCENT },
    mismatchMs: existing?.mismatchMs ?? 900,
    shuffle: existing?.shuffle ?? true,
    ...(existing?.matchedState !== undefined ? { matchedState: existing.matchedState } : {}),
    cards,
  };
  const ops: Ops = [{ op: "setGame", game: base as unknown as GameDecl }];
  // The matched state (if the game uses one) is written explicitly on the new cards.
  const matched = existing?.matchedState;
  if (matched !== undefined)
    for (const id of [a, b]) {
      const el = findElement(doc, id);
      if (el && !Object.hasOwn(el.states ?? {}, matched))
        ops.push({ op: "updateElement", id, set: { states: { ...(el.states ?? {}), [matched]: { opacity: 0.55 } } } });
    }
  return ops;
}

const acceptsOf = (doc: CreativeDocument, id: string): string[] => {
  const d = findElement(doc, id)?.behaviors?.find((b) => b.type === "draggable");
  return d?.type === "draggable" ? [...(d.dropTargets ?? [])] : [];
};

/** Sets (or removes, with null) the draggable behavior of a layer and its accepted zones. */
export function setDraggable(doc: CreativeDocument, id: string, accepts: readonly string[] | null): Ops {
  const el = findElement(doc, id);
  if (!el) return [];
  const others = (el.behaviors ?? []).filter((b) => b.type !== "draggable");
  if (accepts === null)
    return others.length > 0
      ? [{ op: "updateElement", id, set: { behaviors: others } }]
      : [{ op: "updateElement", id, set: {}, unset: ["behaviors"] }];
  const drag: BehaviorUse = { type: "draggable", bounds: "layout", snapBack: true, dropTargets: [...accepts] };
  return [{ op: "updateElement", id, set: { behaviors: [...others, drag] } }];
}

/** The layer becomes a draggable piece that belongs in `zones` (each a drop zone of the game). */
export function makeItem(doc: CreativeDocument, id: string, zones: readonly string[]): Ops {
  const g = doc.game;
  if (!g || !findElement(doc, id)) return [];
  const base = withoutRole(g, id) as Record<string, unknown> & { drops?: GameDecl["drops"] };
  const allZones = [...new Set([...(base.drops?.zones ?? g.drops?.zones ?? []), ...zones])].filter((z) => z !== id);
  const items = [...new Set([...(base.drops?.items ?? g.drops?.items ?? []).filter((x) => x !== id), id])];
  if (allZones.length === 0) return [];
  base.drops = { items, zones: allZones };
  return [{ op: "setGame", game: base as unknown as GameDecl }, ...setDraggable(doc, id, zones)];
}

/** The layer becomes a drop zone of the game (pieces choose it as their place). */
export function makeZone(doc: CreativeDocument, id: string): Ops {
  const g = doc.game;
  if (!g || !findElement(doc, id)) return [];
  const base = withoutRole(g, id) as Record<string, unknown> & { drops?: GameDecl["drops"] };
  const zones = [...new Set([...(base.drops?.zones ?? g.drops?.zones ?? []), id])];
  const items = base.drops?.items ?? g.drops?.items ?? [];
  // A zone with no piece yet is kept in the game only once a piece exists (the block needs both).
  if (items.length === 0) return [];
  base.drops = { items: [...items], zones };
  return [{ op: "setGame", game: base as unknown as GameDecl }];
}

// ─── Placeholder → image ────────────────────────────────────────────────────

/**
 * Replaces a placeholder layer by an image with the SAME id (roles, rules and
 * animations keep pointing at it), the same frames in every variant and the
 * same position in its scene. Text-only parts (content, typography) are dropped;
 * states keep only what applies to an image.
 */
export function replaceWithImage(
  doc: CreativeDocument,
  id: string,
  image: { readonly key: string; readonly asset: AssetRef; readonly alt: string }
): Ops {
  const el = findElement(doc, id);
  const scene = el ? sceneOfElement(doc, id) : undefined;
  if (!el || !scene || el.type === "image" || el.type === "group" || el.type === "component") return [];
  const index = scene.children.indexOf(id);
  // The runner's player (7.6F.1): its placeholder pose colors would paint a box behind the character's
  // art, so an image player keeps only pose changes that never touch the art (opacity, offset, its own pose image).
  const player = doc.game?.runner?.player.element === id;
  const runnerPiece = player || (doc.game?.runner?.spawns ?? []).some((sp) => sp.templates.includes(id));
  const states = el.states
    ? Object.fromEntries(
        Object.entries(el.states).map(([k, p]) => [k, player ? omit(p, "text", "fill", "border") : omit(p, "text")])
      )
    : undefined;
  const replacement = {
    id,
    type: "image",
    name: el.name ?? "Imagen",
    asset: image.key,
    // A runner piece (character, hazard, pickup) shows its whole art, never cropped (7.6F.1).
    fit: runnerPiece ? "contain" : "cover",
    alt: image.alt.slice(0, 300),
    frame: clone(el.frame),
    ...(el.style?.radius !== undefined ? { style: { radius: clone(el.style.radius) } } : {}),
    ...(states ? { states } : {}),
    ...(el.initialState ? { initialState: el.initialState } : {}),
    ...(el.animations ? { animations: clone(el.animations) } : {}),
    ...(el.interactive ? { interactive: clone(el.interactive) } : {}),
    ...(el.behaviors ? { behaviors: clone(el.behaviors) } : {}),
    ...(el.onlyIn ? { onlyIn: [...el.onlyIn] } : {}),
    ...(el.visible !== undefined ? { visible: el.visible } : {}),
    ...(el.timing ? { timing: clone(el.timing) } : {}),
    // A runner spawn template stays a template (7.6F.1).
    ...(el.template === true ? { template: true } : {}),
  } as unknown as Element;
  const ops: Ops = [
    { op: "removeElement", id },
    { op: "addElement", element: replacement, parent: { in: "scene", id: scene.id }, index },
  ];
  for (const layout of doc.layouts) {
    const o = layout.overrides?.[id];
    if (!o) continue;
    const keep: Record<string, unknown> = {};
    for (const k of ["frame", "constraints", "visible", "animations"] as const)
      if (o[k] !== undefined) keep[k] = clone(o[k]);
    if (Object.keys(keep).length > 0)
      ops.push({ op: "setLayoutOverride", layout: layout.id, element: id, override: keep });
  }
  // Variant z-orders that listed the layer keep it at the same place.
  if (scene.childrenByLayout)
    ops.push({ op: "updateScene", id: scene.id, set: { childrenByLayout: clone(scene.childrenByLayout) } });
  return ops;
}

/** Scene where new game layers go by default: the edited scene when it is a game scene, else the first one. */
export function gameScene(doc: CreativeDocument, editing?: string): string | undefined {
  const g = doc.game;
  if (!g) return undefined;
  const current = editingScene(doc, editing).id;
  return g.scenes.includes(current) ? current : g.scenes[0];
}
